import React, { useState, useEffect } from 'react';
import { X, Sparkles, PlayCircle, Users2, Wallet, MessageSquare, Target, HandCoins, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY = 'valnexo:parrainage_intro_dismissed_v2';

const TONES = {
    indigo: { border: 'border-indigo-200', icon: 'bg-indigo-100 text-indigo-700' },
    emerald: { border: 'border-emerald-200', icon: 'bg-emerald-100 text-emerald-700' },
    amber: { border: 'border-amber-200', icon: 'bg-amber-100 text-amber-700' },
    slate: { border: 'border-slate-200', icon: 'bg-slate-100 text-slate-700' },
};

function Block({ icon: Icon, title, tone = 'indigo', children }) {
    const t = TONES[tone] || TONES.indigo;
    return (
        <div className={`rounded-xl border ${t.border} bg-white p-4 space-y-2`}>
            <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${t.icon}`}>
                    <Icon className="h-3.5 w-3.5" />
                </span>
                <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
            </div>
            <div className="text-[13px] text-slate-700 leading-relaxed space-y-1.5">
                {children}
            </div>
        </div>
    );
}

/**
 * Bannière d'annonce du nouveau rôle « Parrain » — VP uniquement, dismissible.
 * Refonte visuelle : structure claire en 4 blocs (Concept · Différences · Votre rôle · Rému)
 * pour éviter la mise en page cassée par les <strong> imbriqués dans des flex.
 */
export function ParrainageIntroBanner({ userRole }) {
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
    });

    useEffect(() => {
        if (dismissed) {
            try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
        }
    }, [dismissed]);

    if (userRole !== 'vp' || dismissed) return null;

    return (
        <div
            data-testid="parrainage-intro-banner"
            className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-5 sm:p-6 shadow-sm"
        >
            <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-indigo-200/40 blur-3xl" />
            <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-purple-200/30 blur-3xl" />

            <button
                type="button"
                onClick={() => setDismissed(true)}
                data-testid="parrainage-intro-dismiss"
                className="absolute right-3 top-3 rounded-full p-1.5 text-indigo-500 hover:bg-indigo-100 transition"
                aria-label="Fermer la bannière"
            >
                <X className="h-4 w-4" />
            </button>

            {/* En-tête */}
            <div className="relative flex items-start gap-3 mb-4">
                <div className="shrink-0 rounded-xl bg-indigo-600 p-2.5 text-white shadow-md">
                    <Sparkles className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1 pr-8">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-600 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                            Nouveau rôle
                        </span>
                        <span className="text-[11px] font-medium text-indigo-700 uppercase tracking-wider">Réseau Parrainage</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 leading-tight">
                        Un vivier d&apos;opportunités, sans la charge de gestion
                    </h3>
                    <p className="mt-1.5 text-sm text-slate-600">
                        Les <span className="font-semibold text-indigo-700">parrains</span> vous envoient des <span className="font-semibold">leads qualifiés</span> — de vraies mises en relation, préparées. Vous traitez les dossiers, ils touchent une commission plus faible que les apporteurs pour leur mise en relation.
                    </p>
                </div>
            </div>

            {/* 4 blocs pédagogiques */}
            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
                <Block icon={Users2} title="Différence avec un apporteur" tone="indigo">
                    <p>Un <span className="font-semibold">apporteur</span> apporte ET gère ses dossiers.</p>
                    <p>Un <span className="font-semibold">parrain</span> apporte uniquement : il présente qui nous sommes, s&apos;assure que vous serez bien accueilli, puis vous prenez le relais.</p>
                </Block>

                <Block icon={MessageSquare} title="Votre rôle de VP" tone="emerald">
                    <p><span className="font-semibold">Trouvez</span> et <span className="font-semibold">sensibilisez</span> vos parrains à faire de vraies mises en relation qualifiées.</p>
                    <p>Créez et animez votre réseau — ils rentrent leurs prospects, vous échangez sur le mur de chaque dossier exactement comme avec vos apporteurs.</p>
                </Block>

                <Block icon={HandCoins} title="Rémunération" tone="amber">
                    <p>Les dossiers parrainage sont traités <span className="font-semibold">comme les dossiers classiques</span>.</p>
                    <p>Les taux de base des parrains sont <span className="font-semibold">déduits de votre part VP</span>.</p>
                </Block>

                <Block icon={Wallet} title="Latitude au cas par cas" tone="slate">
                    <p>Vous pouvez décider de leur donner <span className="font-semibold">plus</span> qu&apos;à la base sur certains parrains N1 — c&apos;est votre choix.</p>
                    <p>Ce sur-plus sort <span className="font-semibold">de votre enveloppe</span> à vous d&apos;arbitrer.</p>
                </Block>
            </div>

            {/* Vidéo à venir + action */}
            <div className="relative mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-indigo-100 bg-white/80 p-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <PlayCircle className="h-5 w-5 text-indigo-600 shrink-0" />
                    <p className="text-sm text-slate-700">
                        Une <span className="font-semibold text-indigo-700">vidéo dédiée</span> arrive bientôt sur l&apos;Académie pour tout expliquer en détail.
                    </p>
                </div>
                <Button
                    size="sm"
                    onClick={() => setDismissed(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                    data-testid="parrainage-intro-understood"
                >
                    <Target className="h-4 w-4 mr-1.5" /> Compris, je le garde en tête
                </Button>
            </div>
        </div>
    );
}

export default ParrainageIntroBanner;
